import { perimetroOutlinePolygon3D } from './PerimetroHelper';

export const getCIMSymbol = (styleKey, color, is3D = false) => {
  if (is3D) {
    const symbolBuilders3D = {
      perimetroPotencial: perimetroPotencial3D,
      perimetroActivo: perimetroActivo3D,
      perimetroEstabilizado: perimetroEstabilizado3D,
      perimetroEstabilizadoMarcas: perimetroEstabilizadoMarcas3D,
    };

    if (!symbolBuilders3D[styleKey]) {
      console.warn(`Estilo 3D '${styleKey}' no encontrado.`);
      return null;
    }

    const a = color?.[3] == null ? 1 : color[3] > 1 ? color[3] / 255 : color[3];
    const colorFinal = [color?.[0] ?? 255, color?.[1] ?? 0, color?.[2] ?? 0, a];

    return symbolBuilders3D[styleKey](colorFinal);
  }

  const finalColor = color || [255, 0, 0, 255];

  const functionSymbols = {
    "sawtooth-line": SITAC_12a,
    "wave-arrow-solid": SITAC_7a,
    "wave-arrow-dotted": SITAC_8a,
    "straight-arrow-solid": SITAC_9a,
    "straight-arrow-dashed": SITAC_10a,
    "loop-arrow": SITAC_11a,
    "filled-triangles": SITAC_12a,
    "hollow-triangles": SITAC_14a,
    "solid-circle-arrow": SITAC_19,
    "dashed-circle-arrow": SITAC_20,
    "solid-open-rectangle": SITAC_21,
    "dotted-open-rectangle": SITAC_21a,
    "dashed-open-rectangle": SITAC_22,
    "dotted-rectangle-loop": SITAC_35a,
  };

  const dataSymbols = {
    fuegoTecnico,
    sector,
    infraPreventiva,
    lineaManual,
    ataqueDirecto,
    viaComunicacion,
    vientoGeneral,
    lineaMecanizada,
    ejePrincipalProp,
    previsto,
    cresta,
    lineaElectrica,
    AC01,
    AC02,
    AC03,
    vaguadas,
    realizado,
    carreraPrincipal,
    carreraSecundaria,
    zonaCriticaPolygon,
    zonaOportunidadPolygon,
    perimetroEstabilizadoPolygon,
    perimetroActivoPolygon,
    perimetroPotencialPolygon,
    zonaCriticaLinea,
    zonaOportunidadLinea,
  };

  const dataColoredSymbols = {
    perimetroPotencial,
    perimetroActivo,
    perimetroEstabilizado,
    solid,
    shortDot,
    shortDash,
    shortDashDotDot,
  };

  let symbolCIM = null;

  // Resolver funciones SITAC
  if (functionSymbols[styleKey]) {
    symbolCIM = functionSymbols[styleKey](finalColor);
  } else if (dataSymbols[styleKey]) {
    symbolCIM = dataSymbols[styleKey].data.symbol;
  } else if (dataColoredSymbols[styleKey]) {
    symbolCIM = dataColoredSymbols[styleKey](finalColor).data.symbol;
  } else {
    console.warn(`Estilo CIM '${styleKey}' no encontrado.`);
    return null;
  }

  return {
    type: "cim",
    data: {
      type: "CIMSymbolReference",
      symbol: symbolCIM,
    },
  };
};

const fondoBlancoDiscreto = {
  type: "CIMSolidStroke",
  enable: true,
  width: 7,
  color: [255, 255, 255, 100],
};

// Fuego Tecnico
export const fuegoTecnico = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // white dashed layer at center of the line
          type: "CIMSolidStroke",
          effects: [
            {
              type: "CIMGeometricEffectWave",
              amplitude: 5,
              period: 5,
              waveform: "Triangle",
            },
          ],
          enable: true, // must be set to true in order for the symbol layer to be visible
          capStyle: "Square",
          joinStyle: "Round",
          width: 1,
          color: [255, 255, 255, 255],
        },
        {
          // lighter red line layer
          type: "CIMSolidStroke",
          enable: false,
          width: 2.5,
          color: [255, 0, 0, 255],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Sector
export const sector = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 10,
          markerPlacement: {
            type: "CIMMarkerPlacementAlongLineSameSize", // places same size markers along the line
            endings: "WithMarkers",
            placementTemplate: [10], // determines space between each arrow
            angleToLine: true, // symbol will maintain its angle to the line when map is rotated
          },
          frame: {
            xmin: -5,
            ymin: -5,
            xmax: 5,
            ymax: 5,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-8, -6],
                    [-2.5, 0],
                    [3, -6],
                    [3, -8],
                    [1, -8],
                    [-2.5, -3],
                    [-6, -8],
                    [-8, -8],
                    [-8, -6],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidFill", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    color: [10, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 10,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            // https://github.com/Esri/cim-spec/blob/master/docs/v2/CIMSymbols.md#enumeration-extremityplacement
            extremityPlacement: "JustBegin",
            angleToLine: true,
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  // Arriba
                  [
                    [-1, 5],
                    [3, 5],
                  ],
                  // Abajo
                  [
                    [-1, -5],
                    [3, -5],
                  ],
                  // Lateral
                  [
                    [3, 5],
                    [3, -5],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    width: 2.5,
                    color: [10, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // lighter red line layer
          type: "CIMSolidStroke",
          enable: true,
          width: 2.5,
          color: [255, 0, 0, 255],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Infra Preventiva
export const infraPreventiva = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAlongLineSameSize", // places same size markers along the line
            endings: "WithMarkers",
            placementTemplate: [12], // determines space between each arrow
            angleToLine: true, // symbol will maintain its angle to the line when map is rotated
          },
          frame: {
            xmin: -5,
            ymin: -5,
            xmax: 5,
            ymax: 5,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-8, 8],
                    [3, 0],
                  ],
                  [
                    [-8, -8],
                    [3, 0],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    width: 2.5,
                    color: [10, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Linea Manual
export const lineaManual = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAlongLineSameSize", // places same size markers along the line
            endings: "WithMarkers",
            placementTemplate: [10], // determines space between each arrow
            angleToLine: true, // symbol will maintain its angle to the line when map is rotated
          },
          frame: {
            xmin: -5,
            ymin: -5,
            xmax: 5,
            ymax: 5,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-8, -8],
                    [-2.5, 0],
                    [3, -8],
                    [-8, -8],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidFill", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    color: [10, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Ataque Directo:
export const ataqueDirecto = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            // https://github.com/Esri/cim-spec/blob/master/docs/v2/CIMSymbols.md#enumeration-extremityplacement
            extremityPlacement: "JustBegin", // JustBegin |JustEnd
            angleToLine: true,
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [0, 5],
                    [0, -5],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    width: 2.5,
                    color: [10, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 10,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            // https://github.com/Esri/cim-spec/blob/master/docs/v2/CIMSymbols.md#enumeration-extremityplacement
            extremityPlacement: "JustEnd", // JustBegin |JustEnd
            angleToLine: true,
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-8, -4],
                    [-8, 4],
                    [2, 0],
                    [-8, -4],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidFill", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    color: [10, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // lighter red line layer
          type: "CIMSolidStroke",
          enable: true,
          width: 2.5,
          color: [10, 10, 10, 255],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Via Comunicacion
export const viaComunicacion = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          type: "CIMSolidStroke",
          effects: [
            {
              type: "CIMGeometricEffectOffset",
              offset: 5,
              method: "Rounded",
              option: "Fast",
            },
          ],
          enable: "true",
          width: 2.5,
          color: [10, 10, 10, 255],
        },
        // LINEA ROJA
        {
          // lighter red line layer
          type: "CIMSolidStroke",
          enable: true,
          width: 2.5,
          color: [10, 10, 10, 255],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Viento General
export const vientoGeneral = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            // https://github.com/Esri/cim-spec/blob/master/docs/v2/CIMSymbols.md#enumeration-extremityplacement
            extremityPlacement: "JustEnd", // JustBegin |JustEnd
            angleToLine: true,
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-8, -4],
                    [-8, 4],
                    [2, 0],
                    [-8, -4],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidFill", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    color: [0, 176, 240, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            // https://github.com/Esri/cim-spec/blob/master/docs/v2/CIMSymbols.md#enumeration-extremityplacement
            extremityPlacement: "JustBegin", // JustBegin |JustEnd
            angleToLine: false,
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-2, 10],
                    [0, 4],
                  ],
                  [
                    [2, 10],
                    [0, 4],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    width: 2,
                    color: [0, 176, 240, 255],
                  },
                ],
              },
            },
          ],
        },
        // LINEA ROJA
        {
          // lighter red line layer
          type: "CIMSolidStroke",
          enable: true,
          width: 3.5,
          color: [0, 176, 240, 255],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};
// Linea Mecanizada
export const lineaMecanizada = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          type: "CIMSolidStroke",
          effects: [
            {
              type: "CIMGeometricEffectOffset",
              offset: 5,
              method: "Rounded",
              option: "Fast",
            },
          ],
          enable: "true",
          width: 2.5,
          color: [10, 10, 10, 255],
        },
        {
          type: "CIMSolidStroke",
          effects: [
            {
              type: "CIMGeometricEffectOffset",
              offset: -5,
              method: "Rounded",
              option: "Fast",
            },
          ],
          enable: "true",
          width: 2.5,
          color: [10, 10, 10, 255],
        },
        // LINEA ROJA
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAlongLineSameSize", // places same size markers along the line
            // endings: 'WithMarkers',
            placementTemplate: [19.5], // determines space between each arrow
            angleToLine: true, // symbol will maintain its angle to the line when map is rotated
          },
          frame: {
            xmin: -5,
            ymin: -5,
            xmax: 5,
            ymax: 5,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-10, 8],
                    [3, -8],
                  ],
                  [
                    [3, 8],
                    [-10, -8],
                  ],
                  [
                    [4, 8],
                    [15, -8],
                  ],
                  [
                    [4, -8],
                    [15, 8],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke",
                    enable: true,
                    width: 2,
                    color: [10, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            // https://github.com/Esri/cim-spec/blob/master/docs/v2/CIMSymbols.md#enumeration-extremityplacement
            extremityPlacement: "JustEnd", // JustBegin |JustEnd
            angleToLine: true,
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-8, -8],
                    [5, 0],
                  ],
                  [
                    [-8, 8],
                    [5, 0],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    width: 2,
                    color: [10, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Eje principal de propagacion
export const ejePrincipalProp = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          type: "CIMSolidStroke",
          effects: [
            {
              type: "CIMGeometricEffectOffset",
              offset: 5,
              method: "Rounded",
              option: "Fast",
            },
          ],
          enable: "true",
          width: 2.5,
          color: [255, 10, 10, 255],
        },
        {
          type: "CIMSolidStroke",
          effects: [
            {
              type: "CIMGeometricEffectOffset",
              offset: -5,
              method: "Rounded",
              option: "Fast",
            },
          ],
          enable: "true",
          width: 2.5,
          color: [255, 10, 10, 255],
        },
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            // https://github.com/Esri/cim-spec/blob/master/docs/v2/CIMSymbols.md#enumeration-extremityplacement
            extremityPlacement: "JustEnd", // JustBegin |JustEnd
            angleToLine: true,
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [0, -8],
                    [13, 0],
                  ],
                  [
                    [0, 8],
                    [13, 0],
                  ],
                  [
                    [0, 8],
                    [0, 4],
                  ],
                  [
                    [0, -8],
                    [0, -4],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    width: 2,
                    color: [255, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Zona critica poligono
export const zonaCriticaPolygon = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMPolygonSymbol",
      symbolLayers: [
        {
          type: "CIMVectorMarker",
          enable: true,
          size: 500,
          markerPlacement: {
            type: "CIMMarkerPlacementInsidePolygon",
            // gridType: 'Fixed',
            stepX: 50,
            stepY: 75,
            clipping: "ClipAtBoundary",
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-25, 32],
                    [0, -32],
                  ],
                  [
                    [25, 32],
                    [0, -32],
                  ],
                ],
              },
              symbol: {
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke",
                    enable: true,
                    width: 2,
                    color: [255, 10, 10, 255],
                  },
                ],
              },
            },
          ],
          scaleSymbolsProportionally: true,
          respectFrame: true,
        },
        {
          // white dashed layer at center of the line
          type: "CIMSolidStroke",
          effects: [
            {
              type: "CIMGeometricEffectWave",
              amplitude: 5,
              period: 5,
              waveform: "Triangle",
            },
          ],
          enable: true, // must be set to true in order for the symbol layer to be visible
          capStyle: "Square",
          joinStyle: "Round",
          width: 1,
          color: [255, 255, 255, 255],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Zona oportunidad poligono
export const zonaOportunidadPolygon = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMPolygonSymbol",
      symbolLayers: [
        {
          type: "CIMVectorMarker",
          enable: true,
          size: 500,
          markerPlacement: {
            type: "CIMMarkerPlacementInsidePolygon",
            // gridType: 'Fixed',
            stepX: 50,
            stepY: 55,
            clipping: "ClipAtBoundary",
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-25, 32],
                    [25, -32],
                  ],
                  [
                    [25, 32],
                    [-25, -32],
                  ],
                ],
              },
              symbol: {
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke",
                    enable: true,
                    width: 2,
                    color: [10, 255, 10, 255],
                  },
                ],
              },
            },
          ],
          scaleSymbolsProportionally: true,
          respectFrame: true,
        },
        {
          type: "CIMSolidStroke",
          enable: true,
          width: 2,
          color: [10, 10, 10, 255],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Perimetro estabilizado poligono
export const perimetroEstabilizadoPolygon = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMPolygonSymbol",
      symbolLayers: [
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAlongLineSameSize", // places same size markers along the line
            endings: "WithMarkers",
            placementTemplate: [12], // determines space between each arrow
            angleToLine: true, // symbol will maintain its angle to the line when map is rotated
            clipping: "ClipAtBoundary",
          },
          frame: {
            xmin: -5,
            ymin: -5,
            xmax: 5,
            ymax: 5,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [0, 8],
                    [0, -8],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    width: 2.5,
                    color: [255, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          type: "CIMSolidFill",
          enable: true,
        },
        {
          // lighter red line layer
          type: "CIMSolidStroke",
          enable: true,
          width: 2.5,
          color: [255, 10, 10, 255],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Perimetro activo poligono
export const perimetroActivoPolygon = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMPolygonSymbol",
      symbolLayers: [
        {
          // lighter red line layer
          type: "CIMSolidStroke",
          enable: true,
          width: 2.5,
          color: [255, 10, 10, 255],
        },
        {
          type: "CIMSolidFill",
          enable: true,
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Perimetro activo poligono
export const perimetroPotencialPolygon = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMPolygonSymbol",
      symbolLayers: [
        {
          // white dashed layer at center of the line
          type: "CIMSolidStroke",
          effects: [
            {
              type: "CIMGeometricEffectDashes",
              dashTemplate: [12, 5], // [ width, space, ..., width, space ]
              lineDashEnding: "NoConstraint",
              controlPointEnding: "NoConstraint",
            },
          ],
          enable: true, // must be set to true in order for the symbol layer to be visible
          capStyle: "Butt",
          joinStyle: "Round",
          width: 2.5,
          color: [255, 0, 0, 255],
        },
        {
          type: "CIMSolidFill",
          enable: true,
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Zona critica linea
export const zonaCriticaLinea = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAlongLineSameSize", // places same size markers along the line
            placementTemplate: [50], // determines space between each arrow
            angleToLine: true, // symbol will maintain its angle to the line when map is rotated
          },
          frame: {
            xmin: -5,
            ymin: -5,
            xmax: 5,
            ymax: 5,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-25, 32],
                    [0, -32],
                  ],
                  [
                    [25, 32],
                    [0, -32],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    width: 2.5,
                    color: [255, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          type: "CIMSolidStroke",
          effects: [
            {
              type: "CIMGeometricEffectOffset",
              offset: 35,
              method: "Rounded",
              option: "Fast",
            },
          ],
          enable: "true",
          width: 2.5,
          color: [10, 10, 10, 255],
        },
        {
          type: "CIMSolidStroke",
          effects: [
            {
              type: "CIMGeometricEffectOffset",
              offset: -35,
              method: "Rounded",
              option: "Fast",
            },
          ],
          enable: "true",
          width: 2.5,
          color: [10, 10, 10, 255],
        },
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            // https://github.com/Esri/cim-spec/blob/master/docs/v2/CIMSymbols.md#enumeration-extremityplacement
            extremityPlacement: "JustEnd", // JustBegin |JustEnd
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [0, 20],
                    [0, -20],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    width: 2,
                    color: [10, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            // https://github.com/Esri/cim-spec/blob/master/docs/v2/CIMSymbols.md#enumeration-extremityplacement
            extremityPlacement: "JustBegin", // JustBegin |JustEnd
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [0, 20],
                    [0, -20],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    width: 2,
                    color: [10, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Zona oportunidad linea
export const zonaOportunidadLinea = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAlongLineSameSize", // places same size markers along the line
            placementTemplate: [50], // determines space between each arrow
            angleToLine: true, // symbol will maintain its angle to the line when map is rotated
          },
          frame: {
            xmin: -5,
            ymin: -5,
            xmax: 5,
            ymax: 5,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-25, 32],
                    [25, -32],
                  ],
                  [
                    [25, 32],
                    [-25, -32],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    width: 2.5,
                    color: [10, 255, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          type: "CIMSolidStroke",
          effects: [
            {
              type: "CIMGeometricEffectOffset",
              offset: 35,
              method: "Rounded",
              option: "Fast",
            },
          ],
          enable: "true",
          width: 2.5,
          color: [10, 10, 10, 255],
        },
        {
          type: "CIMSolidStroke",
          effects: [
            {
              type: "CIMGeometricEffectOffset",
              offset: -35,
              method: "Rounded",
              option: "Fast",
            },
          ],
          enable: "true",
          width: 2.5,
          color: [10, 10, 10, 255],
        },
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            // https://github.com/Esri/cim-spec/blob/master/docs/v2/CIMSymbols.md#enumeration-extremityplacement
            extremityPlacement: "JustEnd", // JustBegin |JustEnd
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [0, 20],
                    [0, -20],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    width: 2,
                    color: [10, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            // https://github.com/Esri/cim-spec/blob/master/docs/v2/CIMSymbols.md#enumeration-extremityplacement
            extremityPlacement: "JustBegin", // JustBegin |JustEnd
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [0, 20],
                    [0, -20],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    width: 2,
                    color: [10, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Carrera principal
export const carreraPrincipal = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            // https://github.com/Esri/cim-spec/blob/master/docs/v2/CIMSymbols.md#enumeration-extremityplacement
            extremityPlacement: "JustEnd", // JustBegin |JustEnd
            angleToLine: true,
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-8, -4],
                    [-8, 4],
                    [2, 0],
                    [-8, -4],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidFill", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    color: [255, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // lighter red line layer
          type: "CIMSolidStroke",
          enable: true,
          width: 6.5,
          color: [255, 10, 10, 255],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Carrera secundaria
export const carreraSecundaria = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            // https://github.com/Esri/cim-spec/blob/master/docs/v2/CIMSymbols.md#enumeration-extremityplacement
            extremityPlacement: "JustEnd", // JustBegin |JustEnd
            angleToLine: true,
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-8, -4],
                    [-8, 4],
                    [2, 0],
                    [-8, -4],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidFill", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    color: [255, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // lighter red line layer
          type: "CIMSolidStroke",
          enable: true,
          width: 3,
          color: [255, 10, 10, 255],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Realizado
export const realizado = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            // https://github.com/Esri/cim-spec/blob/master/docs/v2/CIMSymbols.md#enumeration-extremityplacement
            extremityPlacement: "JustEnd", // JustBegin |JustEnd
            angleToLine: true,
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-8, -4],
                    [-8, 4],
                    [2, 0],
                    [-8, -4],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidFill", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    color: [10, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // lighter red line layer
          type: "CIMSolidStroke",
          enable: true,
          width: 2.5,
          color: [10, 10, 10, 255],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Previsto
export const previsto = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // white dashed layer at center of the line
          type: "CIMSolidStroke",
          effects: [
            {
              type: "CIMGeometricEffectDashes",
              dashTemplate: [10, 5], // [ width, space, ..., width, space ]
              lineDashEnding: "NoConstraint",
              controlPointEnding: "NoConstraint",
            },
          ],
          enable: true, // must be set to true in order for the symbol layer to be visible
          capStyle: "Butt",
          joinStyle: "Round",
          width: 2.5,
          color: [10, 10, 10, 255],
        },
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            // https://github.com/Esri/cim-spec/blob/master/docs/v2/CIMSymbols.md#enumeration-extremityplacement
            extremityPlacement: "JustEnd", // JustBegin |JustEnd
            angleToLine: true,
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-8, -4],
                    [-8, 4],
                    [2, 0],
                    [-8, -4],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidFill", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    color: [10, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Cresta
export const cresta = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // white dashed layer at center of the line
          type: "CIMSolidStroke",
          effects: [
            {
              type: "CIMGeometricEffectDashes",
              dashTemplate: [10, 5, 2, 5], // [ width, space, ..., width, space ]
              lineDashEnding: "NoConstraint",
              controlPointEnding: "NoConstraint",
            },
          ],
          enable: true, // must be set to true in order for the symbol layer to be visible
          capStyle: "Butt",
          joinStyle: "Round",
          width: 2.5,
          color: [10, 10, 10, 255],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Linea electrica
export const lineaElectrica = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // arrow symbol
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAlongLineSameSize", // places same size markers along the line
            endings: "WithMarkers",
            placementTemplate: [20], // determines space between each arrow
            angleToLine: true, // symbol will maintain its angle to the line when map is rotated
          },
          frame: {
            xmin: -5,
            ymin: -5,
            xmax: 5,
            ymax: 5,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-15, 0],
                    [-7, 0],
                  ],
                  [
                    [-2, 2],
                    [2, -2],
                  ],
                  [
                    [-2, -2],
                    [2, 2],
                  ],
                ],
              },
              symbol: {
                // black fill for the arrow symbol
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke", // CIMSolidFill | CIMSolidStroke
                    enable: true,
                    width: 2,
                    color: [10, 10, 10, 255],
                  },
                ],
              },
            },
          ],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

export const AC01 = {
  type: "cim",
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // Línea base
          type: "CIMSolidStroke",
          enable: true,
          width: 13,
          color: [255, 102, 0, 255],
        },
        {
          // Borde de la línea (stroke más grueso debajo)
          type: "CIMSolidStroke",
          enable: true,
          width: 15, // Más ancho que la línea base
          color: [0, 0, 0, 255], // Negro
        },
        {
          // Punta tapar borde
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            extremityPlacement: "JustEnd",
            angleToLine: true,
          },
          frame: {
            xmin: -8,
            ymin: -4,
            xmax: 2,
            ymax: 4,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-6, 3.5],
                    [-2, 3.5],
                    [-2, -3.5],
                    [-6, -3.5],
                    [-6, 3.5],
                  ],
                ],
              },
              symbol: {
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidFill",
                    enable: true,
                    color: [255, 102, 0, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // Flecha en el extremo final de la línea (punta clásica)
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            extremityPlacement: "JustEnd",
            angleToLine: true,
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-1, 5.5],
                    [-1, -5.5],
                    [6, 0],
                    [-1, 5.5],
                  ],
                ],
              },
              symbol: {
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke",
                    enable: true,
                    color: [0, 0, 0, 255], // Negro
                    width: 1,
                  },
                  {
                    type: "CIMSolidFill",
                    enable: true,
                    color: [255, 102, 0, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // Cola "en V" tapar borde
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            extremityPlacement: "JustBegin",
            angleToLine: true,
          },
          frame: {
            xmin: -8,
            ymin: -4,
            xmax: 2,
            ymax: 4,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-8, 3.5],
                    [-4, 3.5],
                    [-4, -3.5],
                    [-8, -3.5],
                    [-8, 3.5],
                  ],
                ],
              },
              symbol: {
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidFill",
                    enable: true,
                    color: [255, 102, 0, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // Cola "en V" al inicio de la línea
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            extremityPlacement: "JustBegin",
            angleToLine: true,
          },
          frame: {
            xmin: -8,
            ymin: -4,
            xmax: 2,
            ymax: 4,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-6, 0],
                    [-6, 3.8],
                    [4, 3.8],
                    [1, 0],
                    [4, -3.8],
                    [-6, -3.8],
                    [-6, 0],
                  ],
                ],
              },
              symbol: {
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke",
                    enable: true,
                    color: [0, 0, 0, 255], // Negro
                    width: 1,
                  },
                  {
                    type: "CIMSolidFill",
                    enable: true,
                    color: [255, 102, 0, 255],
                  },
                ],
              },
            },
          ],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

export const AC02 = {
  type: "cim",
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // Línea base
          type: "CIMSolidStroke",
          enable: true,
          width: 13,
          color: [255, 255, 0, 255],
        },
        {
          // Borde de la línea (stroke más grueso debajo)
          type: "CIMSolidStroke",
          enable: true,
          width: 15, // Más ancho que la línea base
          color: [0, 0, 0, 255], // Negro
        },
        {
          // Punta tapar borde
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            extremityPlacement: "JustEnd",
            angleToLine: true,
          },
          frame: {
            xmin: -8,
            ymin: -4,
            xmax: 2,
            ymax: 4,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-6, 3.5],
                    [-2, 3.5],
                    [-2, -3.5],
                    [-6, -3.5],
                    [-6, 3.5],
                  ],
                ],
              },
              symbol: {
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidFill",
                    enable: true,
                    color: [255, 255, 0, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // Flecha en el extremo final de la línea (punta clásica)
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            extremityPlacement: "JustEnd",
            angleToLine: true,
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-1, 5.5],
                    [-1, -5.5],
                    [6, 0],
                    [-1, 5.5],
                  ],
                ],
              },
              symbol: {
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke",
                    enable: true,
                    color: [0, 0, 0, 255], // Negro
                    width: 1,
                  },
                  {
                    type: "CIMSolidFill",
                    enable: true,
                    color: [255, 255, 0, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // Cola "en V" tapar borde
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            extremityPlacement: "JustBegin",
            angleToLine: true,
          },
          frame: {
            xmin: -8,
            ymin: -4,
            xmax: 2,
            ymax: 4,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-8, 3.5],
                    [-4, 3.5],
                    [-4, -3.5],
                    [-8, -3.5],
                    [-8, 3.5],
                  ],
                ],
              },
              symbol: {
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidFill",
                    enable: true,
                    color: [255, 255, 0, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // Cola "en V" al inicio de la línea
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            extremityPlacement: "JustBegin",
            angleToLine: true,
          },
          frame: {
            xmin: -8,
            ymin: -4,
            xmax: 2,
            ymax: 4,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-6, 0],
                    [-6, 3.8],
                    [4, 3.8],
                    [1, 0],
                    [4, -3.8],
                    [-6, -3.8],
                    [-6, 0],
                  ],
                ],
              },
              symbol: {
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidStroke",
                    enable: true,
                    color: [0, 0, 0, 255], // Negro
                    width: 1,
                  },
                  {
                    type: "CIMSolidFill",
                    enable: true,
                    color: [255, 255, 0, 255],
                  },
                ],
              },
            },
          ],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

export const AC03 = {
  type: "cim",
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // Línea base
          type: "CIMSolidStroke",
          enable: true,
          width: 4,
          color: [43, 42, 41, 255],
        },
        {
          // Flecha en el extremo final de la línea (punta clásica)
          type: "CIMVectorMarker",
          enable: true,
          size: 5,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            extremityPlacement: "JustEnd",
            angleToLine: true,
          },
          frame: {
            xmin: -3,
            ymin: -3,
            xmax: 3,
            ymax: 3,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [-2.5, 5.0],
                    [-2.3, 4.0],
                    [-1.9, 3.0],
                    [-1.5, 2.0],
                    [-1.2, 1.0],
                    [-1.0, 0.0],
                    [-1.2, -1.0],
                    [-1.5, -2.0],
                    [-1.9, -3.0],
                    [-2.3, -4.0],
                    [-2.5, -5.0],
                    [6, 0],
                    [-2.5, 5],
                  ],
                ],
              },
              symbol: {
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidFill",
                    enable: true,
                    color: [43, 42, 41, 255],
                  },
                ],
              },
            },
          ],
        },
        {
          // Tapar borde cola
          type: "CIMVectorMarker",
          enable: true,
          size: 4.2,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            extremityPlacement: "JustBegin",
            angleToLine: true,
          },
          frame: {
            xmin: -8,
            ymin: -4,
            xmax: 2,
            ymax: 4,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [2, 3.5],
                    [-6, 3.5],
                    [-6, -3.5],
                    [2, -3.5],
                    [2, 3.5],
                  ],
                ],
              },
              symbol: {
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidFill",
                    enable: true,
                    color: [43, 42, 41, 255],
                  },
                ],
              },
            },
          ],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

const IT27 = (color) => {
  return {
    type: "CIMLineSymbol",
    symbolLayers: [
      {
        type: "CIMVectorMarker",
        enable: true,
        size: 5,
        markerPlacement: {
          type: "CIMMarkerPlacementAlongLine",
          angleToLine: true,
          placementTemplate: [20],
        },
        frame: { xmin: -10, ymin: -5, xmax: 10, ymax: 20 },
        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-10, 0],
                  [0, 15],
                  [10, 0],
                  [-10, 0],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                {
                  type: "CIMSolidStroke",
                  enable: true,
                  width: 2,
                  color,
                },
                {
                  type: "CIMSolidFill",
                  enable: true,
                  color: [255, 255, 255, 255],
                },
              ],
            },
          },
        ],
      },
      {
        type: "CIMSolidStroke",
        enable: true,
        width: 6,
        color,
      },
      fondoBlancoDiscreto,
    ],
  };
};
const SITAC_7a = (color) => {
  return {
    type: "CIMLineSymbol",
    symbolLayers: [
      {
        type: "CIMVectorMarker",
        enable: true,
        size: 5,
        markerPlacement: {
          type: "CIMMarkerPlacementAtExtremities",
          extremityPlacement: "JustEnd",
          angleToLine: true,
        },
        frame: { xmin: -5, ymin: -5, xmax: 5, ymax: 5 },
        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-5, 5],
                  [5, 0],
                  [-5, -5],
                  [-5, 5],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                { type: "CIMSolidStroke", width: 0, color },
                { type: "CIMSolidFill", color },
              ],
            },
          },
        ],
      },
      {
        type: "CIMSolidStroke",
        enable: true,
        width: 3,
        color,
        effects: [
          {
            type: "CIMGeometricEffectWave",
            amplitude: 5,
            period: 20,
            waveform: "Sinusoid",
          },
        ],
      },
      fondoBlancoDiscreto,
    ],
  };
};

const SITAC_8a = (color) => {
  return {
    type: "CIMLineSymbol",
    symbolLayers: [
      {
        type: "CIMVectorMarker",
        enable: true,
        size: 5,
        markerPlacement: {
          type: "CIMMarkerPlacementAtExtremities",
          extremityPlacement: "JustEnd",
          angleToLine: true,
        },
        frame: { xmin: -5, ymin: -5, xmax: 5, ymax: 5 },
        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-5, 5],
                  [5, 0],
                  [-5, -5],
                  [-5, 5],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                { type: "CIMSolidStroke", width: 0, color },
                { type: "CIMSolidFill", color },
              ],
            },
          },
        ],
      },
      {
        type: "CIMSolidStroke",
        enable: true,
        width: 3,
        color,
        effects: [
          {
            type: "CIMGeometricEffectWave",
            amplitude: 5,
            period: 20,
            waveform: "Sinusoid",
          },
          {
            type: "CIMGeometricEffectDashes",
            dashTemplate: [4, 4],
          },
        ],
      },
      fondoBlancoDiscreto,
    ],
  };
};

const SITAC_9a = (color) => {
  return {
    type: "CIMLineSymbol",
    symbolLayers: [
      {
        type: "CIMVectorMarker",
        enable: true,
        size: 5,
        markerPlacement: {
          type: "CIMMarkerPlacementAtExtremities",
          extremityPlacement: "JustEnd",
          angleToLine: true,
        },
        frame: { xmin: -10, ymin: -10, xmax: 10, ymax: 10 },
        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-10, 10],
                  [10, 0],
                  [-10, -10],
                  [-10, 10],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                { type: "CIMSolidStroke", width: 1, color },
                { type: "CIMSolidFill", color },
              ],
            },
          },
        ],
      },
      { type: "CIMSolidStroke", enable: true, width: 10, color },
      fondoBlancoDiscreto,
    ],
  };
};

const SITAC_10a = (color) => {
  return {
    type: "CIMLineSymbol",
    symbolLayers: [
      {
        type: "CIMVectorMarker",
        enable: true,
        size: 5,
        markerPlacement: {
          type: "CIMMarkerPlacementAtExtremities",
          extremityPlacement: "JustEnd",
          angleToLine: true,
        },
        frame: { xmin: -10, ymin: -10, xmax: 10, ymax: 10 },
        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-10, 10],
                  [10, 0],
                  [-10, -10],
                  [-10, 10],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                { type: "CIMSolidStroke", width: 0, color },
                { type: "CIMSolidFill", color },
              ],
            },
          },
        ],
      },
      {
        type: "CIMSolidStroke",
        enable: true,
        width: 6,
        color,
        effects: [
          {
            type: "CIMGeometricEffectDashes",
            dashTemplate: [20, 15],
            lineDashEnding: "HalfPattern",
            controlPointEnding: "HalfPattern",
          },
        ],
      },
      fondoBlancoDiscreto,
    ],
  };
};

const SITAC_11a = (color) => {
  const finalColor = color || [255, 0, 0, 255];

  return {
    type: "CIMLineSymbol",
    symbolLayers: [
      {
        type: "CIMVectorMarker",
        enable: true,
        size: 5,
        markerPlacement: {
          type: "CIMMarkerPlacementAtExtremities",
          extremityPlacement: "JustEnd",
          angleToLine: true,
        },
        frame: { xmin: -5, ymin: -5, xmax: 5, ymax: 5 },
        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-5, -5],
                  [5, 0],
                  [-5, 5],
                  [-5, -5],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                { type: "CIMSolidStroke", width: 0, color: finalColor },
                { type: "CIMSolidFill", color: finalColor },
              ],
            },
          },
        ],
      },
      {
        type: "CIMVectorMarker",
        enable: true,
        size: 5,
        markerPlacement: {
          type: "CIMMarkerPlacementOnLine",
          angleToLine: true,
          relativeTo: "LineStart",
          step: 120,
        },
        frame: { xmin: -5, ymin: -5, xmax: 5, ymax: 5 },
        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-5, -5],
                  [5, 0],
                  [-5, 5],
                  [-5, -5],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                { type: "CIMSolidStroke", width: 0, color: finalColor },
                { type: "CIMSolidFill", color: finalColor },
              ],
            },
          },
        ],
      },
      {
        type: "CIMSolidStroke",
        enable: true,
        width: 3,
        color: finalColor,
      },
      fondoBlancoDiscreto,
    ],
  };
};
const SITAC_12a = (color) => {
  const finalColor = color || [255, 0, 0, 255];

  const size = 5;

  return {
    type: "CIMLineSymbol",
    symbolLayers: [
      {
        type: "CIMVectorMarker",
        enable: true,
        size,
        markerPlacement: {
          type: "CIMMarkerPlacementAlongLineSameSize",
          angleToLine: true,
          placementTemplate: [size],
        },

        frame: { xmin: -10, ymin: 0, xmax: 10, ymax: 20 },

        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-10, 0],
                  [0, 20],
                  [10, 0],
                  [-10, 0],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                {
                  type: "CIMSolidStroke",
                  enable: true,
                  width: 1,
                  color: finalColor,
                },
                { type: "CIMSolidFill", enable: true, color: finalColor },
              ],
            },
          },
        ],
      },
      fondoBlancoDiscreto,
    ],
  };
};

const SITAC_14a = (color) => {
  const finalColor = color || [0, 155, 0, 255];
  const size = 5;

  return {
    type: "CIMLineSymbol",
    symbolLayers: [
      {
        type: "CIMVectorMarker",
        enable: true,
        size,
        markerPlacement: {
          type: "CIMMarkerPlacementAlongLineSameSize",
          angleToLine: true,
          placementTemplate: [size],
        },
        frame: { xmin: -10, ymin: 0, xmax: 10, ymax: 20 },

        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-10, 0],
                  [0, 20],
                  [10, 0],
                  [-10, 0],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                {
                  type: "CIMSolidStroke",
                  enable: true,
                  width: 2,
                  color: finalColor,
                },
                {
                  type: "CIMSolidFill",
                  enable: true,
                  color: [0, 0, 0, 0],
                },
              ],
            },
          },
        ],
      },
      fondoBlancoDiscreto,
    ],
  };
};

const SITAC_19 = (color) => {
  // SI SE QUIERE HACER QUE NO TENGA RELLNO BLANCO, COMENTAMOS TODO LO QUE PONE WHITECOLOR Y SOLUCIONAMOS LOS ERRORES QUE NOS DE AL COMENTARLO
  const finalColor = color || [255, 0, 0, 255];
  const whiteColor = [255, 255, 255, 255];

  return {
    type: "CIMLineSymbol",
    symbolLayers: [
      {
        type: "CIMSolidStroke",
        enable: true,
        width: 3,
        color: finalColor,
      },

      {
        type: "CIMVectorMarker",
        enable: true,
        size: 5,
        markerPlacement: {
          type: "CIMMarkerPlacementAtExtremities",
          extremityPlacement: "JustEnd",
          angleToLine: true,
        },
        frame: { xmin: -10, ymin: -10, xmax: 10, ymax: 10 },
        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-8, -8],
                  [8, 0],
                  [-8, 8],
                  [-8, -8],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                { type: "CIMSolidStroke", width: 0, color: finalColor },
                { type: "CIMSolidFill", color: finalColor },
              ],
            },
          },
        ],
      },

      {
        type: "CIMVectorMarker",
        enable: true,
        size: 19,

        markerPlacement: {
          type: "CIMMarkerPlacementOnLine",
          angleToLine: false,
          relativeTo: "SegmentMidpoint",
        },

        frame: { xmin: -15, ymin: -15, xmax: 15, ymax: 15 },

        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [0, 13],
                  [5, 12],
                  [9, 9],
                  [12, 5],
                  [13, 0],
                  [12, -5],
                  [9, -9],
                  [5, -12],
                  [0, -13],
                  [-5, -12],
                  [-9, -9],
                  [-12, -5],
                  [-13, 0],
                  [-12, 5],
                  [-9, 9],
                  [-5, 12],
                  [0, 13],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                {
                  type: "CIMSolidStroke",
                  enable: true,
                  width: 3,
                  color: finalColor,
                },
                { type: "CIMSolidFill", enable: true, color: whiteColor },
              ],
            },
          },
        ],
      },
      fondoBlancoDiscreto,
    ],
  };
};
const SITAC_20 = (color) => {
  // SI SE QUIERE HACER QUE NO TENGA RELLNO BLANCO, COMENTAMOS TODO LO QUE PONE WHITECOLOR Y SOLUCIONAMOS LOS ERRORES QUE NOS DE AL COMENTARLO
  const finalColor = color || [255, 0, 0, 255];
  const whiteColor = [255, 255, 255, 255];

  return {
    type: "CIMLineSymbol",
    symbolLayers: [
      {
        type: "CIMSolidStroke",
        enable: true,
        width: 3,
        color: finalColor,
        effects: [
          {
            type: "CIMGeometricEffectDashes",
            dashTemplate: [8, 6],
          },
        ],
      },

      {
        type: "CIMVectorMarker",
        enable: true,
        size: 5,
        markerPlacement: {
          type: "CIMMarkerPlacementAtExtremities",
          extremityPlacement: "JustEnd",
          angleToLine: true,
        },
        frame: { xmin: -10, ymin: -10, xmax: 10, ymax: 10 },
        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-8, -8],
                  [8, 0],
                  [-8, 8],
                  [-8, -8],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                { type: "CIMSolidStroke", width: 0, color: finalColor },
                { type: "CIMSolidFill", color: finalColor },
              ],
            },
          },
        ],
      },

      {
        type: "CIMVectorMarker",
        enable: true,
        size: 19,
        markerPlacement: {
          type: "CIMMarkerPlacementOnLine",
          angleToLine: false,
          relativeTo: "SegmentMidpoint",
        },
        frame: { xmin: -15, ymin: -15, xmax: 15, ymax: 15 },
        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [0, 13],
                  [5, 12],
                  [9, 9],
                  [12, 5],
                  [13, 0],
                  [12, -5],
                  [9, -9],
                  [5, -12],
                  [0, -13],
                  [-5, -12],
                  [-9, -9],
                  [-12, -5],
                  [-13, 0],
                  [-12, 5],
                  [-9, 9],
                  [-5, 12],
                  [0, 13],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                {
                  type: "CIMSolidStroke",
                  enable: true,
                  width: 3,
                  color: finalColor,

                  effects: [
                    {
                      type: "CIMGeometricEffectDashes",
                      dashTemplate: [6, 5],
                    },
                  ],
                },

                {
                  type: "CIMSolidFill",
                  enable: true,
                  color: whiteColor,
                },
              ],
            },
          },
        ],
      },
      fondoBlancoDiscreto,
    ],
  };
};

const SITAC_21 = (color) => {
  return {
    type: "CIMLineSymbol",
    symbolLayers: [
      {
        type: "CIMVectorMarker",
        enable: true,
        size: 5,
        markerPlacement: {
          type: "CIMMarkerPlacementAtRatio",
          position: 0.5,
          angleToLine: true,
        },
        frame: { xmin: -15, ymin: -15, xmax: 15, ymax: 15 },
        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              paths: [
                [
                  [-12, 10],
                  [12, 10],
                ],
              ],
            },
            symbol: {
              type: "CIMLineSymbol",
              symbolLayers: [
                { type: "CIMSolidStroke", enable: true, width: 3, color },
              ],
            },
          },
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-8, 6],
                  [8, 6],
                  [11, 3],
                  [11, -3],
                  [8, -6],
                  [-8, -6],
                  [-11, -3],
                  [-11, 3],
                  [-8, 6],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [{ type: "CIMSolidStroke", width: 3, color }],
            },
          },
        ],
      },
      { type: "CIMSolidStroke", enable: true, width: 0, color: [0, 0, 0, 0] },
      fondoBlancoDiscreto,
    ],
  };
};

const SITAC_21a = (color) => {
  const dashPattern = [4, 4];
  return {
    type: "CIMLineSymbol",
    symbolLayers: [
      {
        type: "CIMVectorMarker",
        enable: true,
        size: 5,
        markerPlacement: {
          type: "CIMMarkerPlacementAtRatio",
          position: 0.5,
          angleToLine: true,
        },
        frame: { xmin: -15, ymin: -15, xmax: 15, ymax: 15 },
        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              paths: [
                [
                  [-12, 10],
                  [12, 10],
                ],
              ],
            },
            symbol: {
              type: "CIMLineSymbol",
              symbolLayers: [
                {
                  type: "CIMSolidStroke",
                  enable: true,
                  width: 3,
                  color,
                  effects: [
                    { type: "CIMDashEffect", dashTemplate: dashPattern },
                  ],
                },
              ],
            },
          },
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-8, 6],
                  [8, 6],
                  [11, 3],
                  [11, -3],
                  [8, -6],
                  [-8, -6],
                  [-11, -3],
                  [-11, 3],
                  [-8, 6],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                {
                  type: "CIMSolidStroke",
                  enable: true,
                  width: 3,
                  color,
                  effects: [
                    { type: "CIMDashEffect", dashTemplate: dashPattern },
                  ],
                },
              ],
            },
          },
        ],
      },
      { type: "CIMSolidStroke", enable: true, width: 0, color: [0, 0, 0, 0] },
      fondoBlancoDiscreto,
    ],
  };
};

const SITAC_22 = (color) => {
  const dashPattern = [4, 4];
  return {
    type: "CIMLineSymbol",
    symbolLayers: [
      {
        type: "CIMVectorMarker",
        enable: true,
        size: 5,
        markerPlacement: {
          type: "CIMMarkerPlacementAtRatio",
          position: 0.5,
          angleToLine: true,
        },
        frame: { xmin: -15, ymin: -15, xmax: 15, ymax: 15 },
        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              paths: [
                [
                  [-12, 10],
                  [12, 10],
                ],
              ],
            },
            symbol: {
              type: "CIMLineSymbol",
              symbolLayers: [
                { type: "CIMSolidStroke", enable: true, width: 3, color },
              ],
            },
          },
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-8, 6],
                  [8, 6],
                  [11, 3],
                  [11, -3],
                  [8, -6],
                  [-8, -6],
                  [-11, -3],
                  [-11, 3],
                  [-8, 6],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                {
                  type: "CIMSolidStroke",
                  enable: true,
                  width: 3,
                  color,
                  effects: [
                    { type: "CIMDashEffect", dashTemplate: dashPattern },
                  ],
                },
              ],
            },
          },
        ],
      },
      { type: "CIMSolidStroke", enable: true, width: 0, color: [0, 0, 0, 0] },
      fondoBlancoDiscreto,
    ],
  };
};

const SITAC_35a = (color) => {
  const finalColor = color || [255, 0, 0, 255];

  return {
    type: "CIMLineSymbol",
    symbolLayers: [
      {
        type: "CIMVectorMarker",
        enable: true,
        size: 5,
        markerPlacement: {
          type: "CIMMarkerPlacementAtExtremities",
          extremityPlacement: "JustEnd",
          angleToLine: true,
        },
        frame: { xmin: -5, ymin: -5, xmax: 5, ymax: 5 },
        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-5, -5],
                  [5, 0],
                  [-5, 5],
                  [-5, -5],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                { type: "CIMSolidStroke", width: 0, color: finalColor },
                { type: "CIMSolidFill", color: finalColor },
              ],
            },
          },
        ],
      },

      {
        type: "CIMVectorMarker",
        enable: true,
        size: 5,
        markerPlacement: {
          type: "CIMMarkerPlacementOnLine",
          angleToLine: true,
          relativeTo: "LineStart",
          step: 120,
        },
        frame: { xmin: -5, ymin: -5, xmax: 5, ymax: 5 },
        markerGraphics: [
          {
            type: "CIMMarkerGraphic",
            geometry: {
              rings: [
                [
                  [-5, -5],
                  [5, 0],
                  [-5, 5],
                  [-5, -5],
                ],
              ],
            },
            symbol: {
              type: "CIMPolygonSymbol",
              symbolLayers: [
                { type: "CIMSolidStroke", width: 0, color: finalColor },
                { type: "CIMSolidFill", color: finalColor },
              ],
            },
          },
        ],
      },

      {
        type: "CIMSolidStroke",
        enable: true,
        width: 2,
        color: finalColor,

        effects: [
          {
            type: "CIMGeometricEffectDashes",
            dashTemplate: [8, 6],
          },
        ],
      },
      fondoBlancoDiscreto,
    ],
  };
};
const applyColorToStatic = (staticSymbolObj, color) => {
  if (!staticSymbolObj || !staticSymbolObj.data || !staticSymbolObj.data.symbol)
    return null;

  const symbol = JSON.parse(JSON.stringify(staticSymbolObj.data.symbol));
  const finalColor = color || [0, 0, 0, 255];

  if (symbol.symbolLayers) {
    symbol.symbolLayers.forEach((layer) => {
      // Importante: No aplicamos el color dinámico al fondo blanco
      if (layer === fondoBlancoDiscreto) return;

      if (layer.color) layer.color = finalColor;

      if (layer.markerGraphics) {
        layer.markerGraphics.forEach((mg) => {
          if (mg.symbol && mg.symbol.symbolLayers) {
            mg.symbol.symbolLayers.forEach((subLayer) => {
              if (subLayer.color) subLayer.color = finalColor;
            });
          }
        });
      }
    });
  }
  return symbol;
};

// Vaguadas
export const vaguadas = {
  type: "cim",
  // CIM Line Symbol
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          // white dashed layer at center of the line
          type: "CIMSolidStroke",
          effects: [
            {
              type: "CIMGeometricEffectDashes",
              dashTemplate: [12, 5], // [ width, space, ..., width, space ]
              lineDashEnding: "NoConstraint",
              controlPointEnding: "NoConstraint",
            },
          ],
          enable: true, // must be set to true in order for the symbol layer to be visible
          capStyle: "Butt",
          joinStyle: "Round",
          width: 2.5,
          color: [0, 176, 240, 255],
        },
        fondoBlancoDiscreto,
      ],
    },
  },
};

// Perimetro potencial
const perimetroPotencial = (color) => {
  return {
    type: "cim",
    // CIM Line Symbol
    data: {
      type: "CIMSymbolReference",
      symbol: {
        type: "CIMLineSymbol",
        symbolLayers: [
          {
            // white dashed layer at center of the line
            type: "CIMSolidStroke",
            effects: [
              {
                type: "CIMGeometricEffectDashes",
                dashTemplate: [12, 5], // [ width, space, ..., width, space ]
                lineDashEnding: "NoConstraint",
                controlPointEnding: "NoConstraint",
              },
            ],
            enable: true, // must be set to true in order for the symbol layer to be visible
            capStyle: "Butt",
            joinStyle: "Round",
            width: 2.5,
            color,
          },
          fondoBlancoDiscreto,
        ],
      },
    },
  };
};

// Perimetro activo
const perimetroActivo = (color) => {
  return {
    type: "cim",
    // CIM Line Symbol
    data: {
      type: "CIMSymbolReference",
      symbol: {
        type: "CIMLineSymbol",
        symbolLayers: [
          {
            // lighter red line layer
            type: "CIMSolidStroke",
            enable: true,
            width: 2.5,
            color,
          },
          fondoBlancoDiscreto,
        ],
      },
    },
  };
};

// Perimetro estabilizado
const perimetroEstabilizado = (color) => {
  return {
    type: "cim",
    // CIM Line Symbol
    data: {
      type: "CIMSymbolReference",
      symbol: {
        type: "CIMLineSymbol",
        symbolLayers: [
          {
            // arrow symbol
            type: "CIMVectorMarker",
            enable: true,
            size: 10,
            markerPlacement: {
              type: "CIMMarkerPlacementAlongLineSameSize", // places same size markers along the line
              endings: "WithMarkers",
              placementTemplate: [12], // determines space between each arrow
              angleToLine: true, // symbol will maintain its angle to the line when map is rotated
            },
            frame: {
              xmin: -5,
              ymin: -5,
              xmax: 5,
              ymax: 5,
            },
            markerGraphics: [
              {
                type: "CIMMarkerGraphic",
                geometry: {
                  rings: [
                    [
                      [0, 8],
                      [0, -8],
                    ],
                  ],
                },
                symbol: {
                  // black fill for the arrow symbol
                  type: "CIMPolygonSymbol",
                  symbolLayers: [
                    {
                      type: "CIMSolidStroke", // CIMSolidFill | CIMSolidStroke
                      enable: true,
                      width: 2.5,
                      color,
                    },
                  ],
                },
              },
            ],
          },
          {
            // lighter red line layer
            type: "CIMSolidStroke",
            enable: true,
            width: 2.5,
            color,
          },
          fondoBlancoDiscreto,
        ],
      },
    },
  };
};

const makeStroke = ({
  color,
  width = 2.5,
  dashTemplate = null,
  capStyle = "Round",
}) => {
  const stroke = {
    type: "CIMSolidStroke",
    enable: true,
    width,
    color,
    capStyle,
  };

  if (dashTemplate) {
    stroke.effects = [
      {
        type: "CIMGeometricEffectDashes",
        dashTemplate,
      },
    ];
  }

  return stroke;
};

const solid = (color) => {
  return {
    type: "cim",
    data: {
      type: "CIMSymbolReference",
      symbol: {
        type: "CIMLineSymbol",
        symbolLayers: [
          makeStroke({ color, dashTemplate: null }),
          fondoBlancoDiscreto,
        ],
      },
    },
  };
};

const DASH = 6;
const DOT = 1;

const GAP_DASH = 5;
const GAP_DOT = 5;
const GAP_BETWEEN_DOTS = 5;

const shortDash = (color) => ({
  type: "cim",
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        makeStroke({ color, dashTemplate: [DASH, GAP_DASH] }),
        fondoBlancoDiscreto,
      ],
    },
  },
});

const shortDot = (color) => ({
  type: "cim",
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        makeStroke({ color, dashTemplate: [DOT, GAP_DOT], capStyle: "Round" }),
        fondoBlancoDiscreto,
      ],
    },
  },
});

const shortDashDotDot = (color) => ({
  type: "cim",
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        makeStroke({
          color,
          capStyle: "Round",
          dashTemplate: [DASH, GAP_DASH, DOT, GAP_BETWEEN_DOTS, DOT, GAP_DASH],
        }),
        fondoBlancoDiscreto,
      ],
    },
  },
});

// 3D ------------------------------------------------------------------
// Pared
export const infraPreventiva3D = {
  type: "line-3d",
  symbolLayers: [
    {
      type: "path",
      profile: "quad",
      width: 5,
      height: 100,
      material: { color: "#ff7380" },
      cap: "square",
      profileRotation: "heading",
    },
  ],
};

export const punta3D = {
  type: "point-3d",
  symbolLayers: [
    {
      type: "object",
      resource: { primitive: "cone" },
      material: { color: "#ff7380" },
      width: 8,
      height: 200,
      depth: 150,
    },
  ],
};

// Cilindro
export const viaComunicacion3D = {
  type: "point-3d",
  symbolLayers: [
    {
      type: "object",
      width: 100,
      height: 100,
      resource: {
        primitive: "cylinder",
      },
      material: {
        color: "#000000",
        outline: {
          color: "#ffffff",
        },
      },
    },
  ],
};

// Piramide
export const sector3D = {
  type: "point-3d",
  symbolLayers: [
    {
      type: "object",
      resource: { primitive: "tetrahedron" },
      width: 100,
      height: 70,
      depth: 100,
      material: { color: "#FFA500" },
    },
  ],
};

// Cono
export const vientoGeneral3D = {
  type: "point-3d",
  symbolLayers: [
    {
      type: "object",
      resource: { primitive: "cone" },
      material: { color: "#ff7380" },
      width: 200,
      height: 200,
      depth: 150,
    },
  ],
};

// Zona
export const zonaCriticaPolygon3D = {
  type: "polygon-3d",
  symbolLayers: [
    {
      type: "extrude",
      size: 70, // Meters in height
      material: { color: [50, 100, 200, 0.5] },
    },
  ],
};

// Perimetros
export const perimetroPotencial3D = (colorFinal) => perimetroOutlinePolygon3D(colorFinal, { type: 'style', style: 'dash' });
export const perimetroActivo3D = (colorFinal) => perimetroOutlinePolygon3D(colorFinal);
export const perimetroEstabilizado3D = (colorFinal) => perimetroOutlinePolygon3D(colorFinal);

export const perimetroEstabilizadoMarcas3D = (colorFinal) => ({
  type: 'line-3d',
  symbolLayers: [
    {
      type: 'line',
      size: 3,
      cap: 'butt',
      material: { color: colorFinal },
    },
  ],
});
